/**
 * LoginView — sign-in gate.
 *
 * Shown when AuthProvider reports `signed-out`. Email + password and a
 * Google button. Validation is inline and runs before any submit so we
 * never make a network round trip on obvious form errors.
 */

import { useState, type FormEvent } from 'react';
import { LogIn, Mail, Lock, AlertTriangle, ArrowRight } from 'lucide-react';
import { GlassPanel } from '../components/GlassPanel';
import { C } from '../lib/tokens';
import { useAuth } from '../lib/auth/AuthProvider';

interface FieldErrors {
  email?: string;
  password?: string;
}

export function LoginView() {
  const { signIn, signInWithGoogle, continueWithoutAuth } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'email' | 'google' | 'bypass' | null>(null);

  const validate = (): FieldErrors => {
    const next: FieldErrors = {};
    if (!email.trim()) next.email = 'Email is required.';
    else if (!/.+@.+\..+/.test(email)) next.email = 'Use a full email address.';
    if (!password) next.password = 'Password is required.';
    else if (password.length < 6) next.password = 'At least 6 characters.';
    return next;
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    const fe = validate();
    setErrors(fe);
    if (Object.keys(fe).length > 0) return;
    setBusy('email');
    try {
      await signIn(email, password);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Something went wrong.';
      setSubmitError(msg);
    } finally {
      setBusy(null);
    }
  };

  const onGoogle = async () => {
    setSubmitError(null);
    setBusy('google');
    try {
      await signInWithGoogle();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not sign in.';
      setSubmitError(msg);
    } finally {
      setBusy(null);
    }
  };

  // TEMPORARY: skip auth entirely and drop into the portal as admin.
  const onContinue = async () => {
    setSubmitError(null);
    setBusy('bypass');
    try {
      await continueWithoutAuth();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not continue.';
      setSubmitError(msg);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="login-view">
      <GlassPanel variant="hero" className="login-panel">
        <header className="login-panel__head">
          <span className="t-eyebrow" style={{ color: C.magentaSoft }}>
            Argo OS
          </span>
          <h1 className="t-page-title" style={{ margin: '6px 0 0' }}>
            Sign in
          </h1>
          <p style={{ margin: '4px 0 0', color: C.textMuted }} className="t-body">
            Internal tool. Use your Argo IQ credentials.
          </p>
        </header>

        <form className="login-form" onSubmit={onSubmit} noValidate>
          <div className="login-field">
            <label htmlFor="login-email" className="t-eyebrow">
              <Mail size={11} /> Email
            </label>
            <input
              id="login-email"
              className="glass-input"
              type="email"
              autoComplete="email"
              placeholder="you@argoiq.com"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (errors.email) setErrors({ ...errors, email: undefined });
              }}
              aria-invalid={Boolean(errors.email)}
              aria-describedby={errors.email ? 'login-email-error' : undefined}
              disabled={busy !== null}
            />
            {errors.email && (
              <span id="login-email-error" className="login-field__error" role="alert">
                {errors.email}
              </span>
            )}
          </div>

          <div className="login-field">
            <label htmlFor="login-password" className="t-eyebrow">
              <Lock size={11} /> Password
            </label>
            <input
              id="login-password"
              className="glass-input"
              type="password"
              autoComplete="current-password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (errors.password) setErrors({ ...errors, password: undefined });
              }}
              aria-invalid={Boolean(errors.password)}
              aria-describedby={errors.password ? 'login-password-error' : undefined}
              disabled={busy !== null}
            />
            {errors.password && (
              <span id="login-password-error" className="login-field__error" role="alert">
                {errors.password}
              </span>
            )}
          </div>

          {submitError && (
            <div className="login-banner" role="alert">
              <AlertTriangle size={13} /> {submitError}
            </div>
          )}

          <button
            type="submit"
            className="glass-btn glass-btn--primary"
            disabled={busy !== null}
            style={{ justifyContent: 'center' }}
          >
            <LogIn size={13} />
            {busy === 'email' ? 'Signing you in…' : 'Sign in'}
          </button>

          <div className="login-divider">
            <span>or</span>
          </div>

          <button
            type="button"
            className="glass-btn"
            onClick={onGoogle}
            disabled={busy !== null}
            style={{ justifyContent: 'center' }}
          >
            <GoogleGlyph />
            {busy === 'google' ? 'Opening Google…' : 'Continue with Google'}
          </button>

          {/* TEMPORARY login bypass — enters the portal as admin without
              credentials. Remove once real auth is required. */}
          <div className="login-divider">
            <span>skip for now</span>
          </div>

          <button
            type="button"
            className="glass-btn glass-btn--primary"
            onClick={onContinue}
            disabled={busy !== null}
            style={{ justifyContent: 'center' }}
          >
            {busy === 'bypass' ? 'Entering…' : 'Continue'}
            <ArrowRight size={14} />
          </button>
          <p className="login-hint" style={{ textAlign: 'center', marginTop: 4 }}>
            Temporary — bypasses sign-in and opens the portal directly.
          </p>

          <p className="login-hint">
            Need an account? Ask an admin to invite you. Argo IQ uses
            domain-restricted access.
          </p>
        </form>

        <details className="login-mockcreds">
          <summary>Demo credentials (local mock)</summary>
          <ul>
            <li>
              <code>grant@argoiq.com</code> / <code>argoiq</code> · admin
            </li>
            <li>
              <code>patrick@argoiq.com</code> / <code>argoiq</code> · staff
            </li>
            <li>
              <code>viewer@argoiq.com</code> / <code>argoiq</code> · viewer
            </li>
          </ul>
          <p className="t-meta" style={{ marginTop: 8 }}>
            These disappear once Firebase Auth is wired (see{' '}
            <code>implementationplan.md</code> §7).
          </p>
        </details>
      </GlassPanel>
    </div>
  );
}

// Inline SVG so we don't ship a brand asset for a single button.
function GoogleGlyph() {
  return (
    <svg width="14" height="14" viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#FFC107"
        d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.1 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"
      />
      <path
        fill="#FF3D00"
        d="M6.3 14.7l6.6 4.8C14.7 15.7 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.1 29.5 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.4 0 10.3-2.1 14-5.5l-6.5-5.3c-2 1.4-4.6 2.3-7.5 2.3-5.3 0-9.7-3.4-11.3-8l-6.6 5.1C9.6 39.7 16.3 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.5l6.5 5.3c-.5.4 7-5.1 7-14.8 0-1.3-.1-2.4-.4-3.5z"
      />
    </svg>
  );
}
